import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import { useTranslation } from 'react-i18next'
import { useParams, useNavigate } from 'react-router-dom'
import { 
  Calendar, 
  MapPin, 
  Clock, 
  Users, 
  Share2, 
  ChevronDown, 
  ChevronUp, 
  UserPlus, 
  AlertCircle,
  Check,
  Zap,
  X
} from 'lucide-react'
import { getEvent, getPublicEvent } from 'api'
import { getAvatarUrl } from '../../../../api/utils'
import QRCode from 'react-qr-code'
import EventOnboarding from '../eventOnboarding/view'
import EventModalParticipant from '../eventModalParticipant/view'
import {
  Container,
  BackgroundOrb,
  ContentWrapper,
  HeroSection,
  HeroContent,
  HeroGrid,
  HeroMain,
  HeroAside,
  EventHeader,
  EventIcon,
  EventInfo,
  DateInfo,
  DateText,
  EventTitle,
  VenueInfo,
  VenueLink,
  StatsContainer,
  StatBadge,
  ShareCard,
  ShareHeader,
  ShareTitle,
  QRContainer,
  DetailsSection,
  ExpandButton,
  CollapsibleContent,
  DetailsGrid,
  DetailCard,
  DetailTitle,
  DetailContent,
  ParticipantsSection,
  ParticipantsHeader,
  ParticipantsIcon,
  ParticipantsTitle,
  ParticipantsGrid,
  ParticipantCard,
  ParticipantBg,
  StatusBadge,
  ParticipantContent,
  ParticipantAvatar,
  AvatarImage,
  ParticipantName,
  ParticipantAlias,
  FloatingButton,
  JoinButton,
  FullButton,
  ModalOverlay,
  ModalContent,
  ModalCloseButton,
  ModalTitle,
  ModalDescription,
  ModalPlaceholder,
  SnackbarContainer
} from './styled'

const EventParticipant = () => {
  const { t } = useTranslation()
  const params = useParams()
  const navigate = useNavigate()
  const { slug } = params
  
  const [theParticipants, setParticipants] = useState([])
  const [myTicket, setMyTicket] = useState([])
  const [theEvent, setEvent] = useState([])
  const [userModal, setUserModal] = useState(false)
  const [joinModal, setJoinModal] = useState(false)
  const [eventFull, setEventFull] = useState(false)
  const [selectedParticipant, setSelectedParticipant] = useState(null)
  const [loggedIn, setLoggedIn] = useState(localStorage.getItem('token') !== null)
  const [anonymous, setAnonymous] = useState(true)
  const [showDetails, setShowDetails] = useState(false)
  const [openSnack, setOpenSnack] = useState("")

  // Date formatting functions
  const formatDate = (dateTimeString) => {
    const date = new Date(dateTimeString)
    return `${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`
  }

  const formatDateFull = (dateTimeString) => {
    const daysOfWeek = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
    const date = new Date(dateTimeString)
    const dayOfWeek = daysOfWeek[date.getDay()]
    return `${dayOfWeek}, ${date.getDate()}-${date.getMonth() + 1}-${date.getFullYear()}`
  }

  const formatTime = (dateTimeString) => {
    const date = new Date(dateTimeString)
    return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })
  }

  const loadModal = (participant) => {
    setSelectedParticipant(participant)
    setUserModal(true)
  }

  const getEventInfo = () => {
    if (loggedIn) {      
      getEvent(slug).then(r => {
        setEvent(r.event)
        setParticipants(r.event?.active_tickets)
        if (r.event?.my_ticket !== null) {
          navigate(`/event-ticket/${r.event?.my_ticket?.slug}`);
        }
        setMyTicket(r.event?.my_ticket)
        if (r.event?.space_limit <= r.event?.active_tickets?.length) {
          setEventFull(true)
        }
        setAnonymous(false)
      })
        .catch(console.error)
    } else {
      getPublicEvent(slug).then(r => {
        setEvent(r.event)
        setParticipants(r.event.active_tickets)
        setMyTicket(r.event.my_ticket)
      })
        .catch(console.error)
    }
  }

  useEffect(() => {
    getEventInfo()
  }, [])

  const toggleDetails = () => setShowDetails(prev => !prev)

  const formattedDate = formatDate(theEvent.start)
  const formattedDateFull = formatDateFull(theEvent.start)
  const formattedStart = formatTime(theEvent.start)
  const formattedEnd = formatTime(theEvent.end)
  
  const baseUrl = window.location.origin
  const shareUrl = `${baseUrl}/events/${theEvent.slug}`

  const ParticipantCardComponent = ({ participant, anonymous }) => {
    const avatarSrc = !anonymous ? getAvatarUrl(participant?.user, null) : null

    const getInitials = () => {
      if (anonymous) return '?'
      const firstName = participant?.user?.first_name || ''
      const lastName = participant?.user?.last_name || ''
      return (firstName.charAt(0) + (lastName.charAt(0) || '')).toUpperCase()
    }

    const handleImageError = (e) => {
      e.target.style.display = 'none'
    }

    return (
      <ParticipantCard
        onClick={() => !anonymous && loadModal(participant)}
        anonymous={anonymous}
      >
        <ParticipantBg />
        
        {participant?.status === 'AC' && (
          <StatusBadge>
            <Check size={16} color="white" />
          </StatusBadge>
        )}

        <ParticipantContent>
          <ParticipantAvatar anonymous={anonymous}>
            {avatarSrc ? (
              <AvatarImage 
                src={avatarSrc} 
                alt={participant?.user?.first_name}
                onError={handleImageError}
              />
            ) : (
              getInitials()
            )}
          </ParticipantAvatar>
          
          <ParticipantName>
            {anonymous ? 'Anonymous' : participant?.user?.first_name}
          </ParticipantName>
          
          <ParticipantAlias>
            {anonymous ? 'Guest' : participant?.user?.alias || 'Member'}
          </ParticipantAlias>
        </ParticipantContent>
      </ParticipantCard>
    )
  }

  const QRCodeComponent = ({ value, size }) => (
    <QRContainer size={size}>
      <QRCode 
        value={value} 
        size={size - 16}
        style={{
          background: 'white',
          padding: '4px',
          borderRadius: '4px'
        }}
      />
    </QRContainer>
  )

  const SnackbarComponent = ({ isOpen, message, onClose }) => {
    useEffect(() => {
      if (isOpen) {
        const timer = setTimeout(() => {
          onClose()
        }, 3000)
        return () => clearTimeout(timer)
      }
    }, [isOpen, onClose])

    return (
      <SnackbarContainer isOpen={isOpen}>
        {message}
      </SnackbarContainer>
    )
  }

  return (
    <Container>
      {/* Animated Background Elements */}
      <BackgroundOrb className="orb-1" duration="8s" />
      <BackgroundOrb className="orb-2" duration="6s" reverse />

      <ContentWrapper>
        {/* Hero Section */}
        <HeroSection>
          <HeroContent>
            <HeroGrid>
              <HeroMain>
                <EventHeader>
                  <EventIcon>
                    <Zap size={48} />
                  </EventIcon>
                  
                  <EventInfo>
                    <DateInfo>
                      <Calendar size={20} color="#10b981" />
                      <DateText>
                        {formattedDate}, {formattedStart} - {formattedEnd}
                      </DateText>
                    </DateInfo>
                    
                    <EventTitle>
                      {theEvent?.name}
                    </EventTitle>
                    
                    <VenueInfo>
                      <MapPin size={16} color="#f59e0b" />
                      <VenueLink 
                        href={theEvent?.venue?.gmaps_address} 
                        target="_blank" 
                        rel="noopener"
                      >
                        {theEvent?.venue?.name} - {theEvent?.venue?.single_address}
                      </VenueLink>
                    </VenueInfo>
                  </EventInfo>
                </EventHeader>

                {/* Quick Stats */}
                <StatsContainer>
                  <StatBadge color="green">
                    <Users size={18} color="#10b981" />
                    <span>{theParticipants.length} attending</span>
                  </StatBadge>
                  
                  <StatBadge color="orange">
                    <Clock size={18} color="#f59e0b" />
                    <span>
                      {Math.round((new Date(theEvent.end) - new Date(theEvent.start)) / (1000 * 60 * 60))} hours
                    </span>
                  </StatBadge>
                </StatsContainer>
              </HeroMain>

              <HeroAside>
                <ShareCard>
                  <ShareHeader>
                    <Share2 size={20} color="#8b5cf6" />
                    <ShareTitle>Share Event</ShareTitle>
                  </ShareHeader>
                  <QRCodeComponent value={shareUrl} size={120} />
                </ShareCard>
              </HeroAside>
            </HeroGrid>

            {/* Expandable Details */}
            <DetailsSection>
              <ExpandButton onClick={toggleDetails}>
                {showDetails ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                {showDetails ? 'Less' : 'More'} details
              </ExpandButton>

              <CollapsibleContent isOpen={showDetails}>
                <DetailsGrid>
                  {theEvent?.description && (
                    <DetailCard>
                      <DetailTitle color="#10b981">About the event</DetailTitle>
                      <DetailContent>{theEvent.description}</DetailContent>
                    </DetailCard>
                  )}

                  {theEvent?.group?.description && (
                    <DetailCard>
                      <DetailTitle color="#f59e0b">About us</DetailTitle>
                      <DetailContent>{theEvent.group.description}</DetailContent>
                    </DetailCard>
                  )}

                  {theEvent?.instructions && theEvent.instructions !== "" && (
                    <DetailCard>
                      <DetailTitle color="#8b5cf6">General Information</DetailTitle>
                      <DetailContent dangerouslySetInnerHTML={{ __html: theEvent.instructions }} />
                    </DetailCard>
                  )}

                  {theEvent?.rules && theEvent.rules !== "" && (
                    <DetailCard>
                      <DetailTitle color="#ef4444">Basic Rules</DetailTitle>
                      <DetailContent dangerouslySetInnerHTML={{ __html: theEvent.rules }} />
                    </DetailCard>
                  )}

                  <DetailCard>
                    <DetailTitle color="#06b6d4">When</DetailTitle>
                    <DetailContent>
                      {formattedDateFull}, {formattedStart} - {formattedEnd}
                    </DetailContent>
                  </DetailCard>

                  <DetailCard>
                    <DetailTitle color="#f59e0b">Where</DetailTitle>
                    <DetailContent>
                      <VenueLink 
                        href={theEvent?.venue?.gmaps_address} 
                        target="_blank" 
                        rel="noopener"
                      >
                        {theEvent?.venue?.single_address}
                      </VenueLink>
                    </DetailContent>
                  </DetailCard>
                </DetailsGrid>
              </CollapsibleContent>
            </DetailsSection>
          </HeroContent>
        </HeroSection>

        {/* Participants Section */}
        <ParticipantsSection>
          <ParticipantsHeader>
            <ParticipantsIcon>
              <Users size={20} color="white" />
            </ParticipantsIcon>
            <ParticipantsTitle>
              Event Attendees ({theParticipants.length})
            </ParticipantsTitle>
          </ParticipantsHeader>

          <ParticipantsGrid>
            {theParticipants.map((participant, index) => (
              <ParticipantCardComponent 
                key={index} 
                participant={participant} 
                anonymous={anonymous} 
              />
            ))}
          </ParticipantsGrid>
        </ParticipantsSection>

        {/* Floating Action Button */}
        <FloatingButton>
          {!eventFull ? (
            <JoinButton onClick={() => setJoinModal(true)}>
              <UserPlus size={20} />
              {t('event.join')}
            </JoinButton>
          ) : (
            <FullButton>
              <AlertCircle size={20} />
              {t('event.full')}
            </FullButton>
          )}
        </FloatingButton>
      </ContentWrapper>

      {/* Modals */}
      <EventModalParticipant 
        selectedParticipant={selectedParticipant}  
        myTicket={myTicket} 
        visibleModal={userModal} 
        onClose={() => setUserModal(false)} 
      />

      <ModalOverlay 
        isOpen={joinModal} 
        onClick={() => setJoinModal(false)}
      >
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalCloseButton onClick={() => setJoinModal(false)}>
            <X size={24} />
          </ModalCloseButton>
          
          <ModalTitle>Join {theEvent?.name}</ModalTitle>
          <ModalDescription>
            Complete your registration to secure your spot at this amazing event!
          </ModalDescription>
          
          <EventOnboarding event={theEvent} />
        </ModalContent>
      </ModalOverlay>

      <SnackbarComponent 
        isOpen={openSnack !== ""} 
        message={openSnack} 
        onClose={() => setOpenSnack("")} 
      />
    </Container>
  )
}

EventParticipant.propTypes = {
  // Add any props if needed
}

EventParticipant.defaultProps = {
  // Add default props if needed
}

export default EventParticipant